import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, Unlock } from 'lucide-react';
import { warehouseAPI } from '../../api/warehouses';

interface UnlockConfirmModalProps {
  lock: any;
  onClose: () => void;
  onSuccess: () => void;
}

const UnlockConfirmModal: React.FC<UnlockConfirmModalProps> = ({
  lock,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [unlockReason, setUnlockReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!unlockReason.trim()) {
      setError(t('warehouse.periodLock.reasonRequired'));
      return;
    }

    setLoading(true);
    try {
      await warehouseAPI.unlockPeriod(lock.id, {
        unlockReason: unlockReason.trim(),
      });

      onSuccess();
    } catch (err: any) {
      console.error('Failed to unlock period:', err);
      setError(err.response?.data?.message || t('common.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Unlock className="w-6 h-6 text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-800">
              {t('warehouse.periodLock.unlock')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Warning */}
          <div className="flex items-start gap-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-orange-800">
              <p className="font-medium mb-1">{t('common.warning')}</p>
              <p>{t('warehouse.periodLock.unlockWarning')}</p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-4 py-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Lock Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{t('warehouse.periodLock.warehouse')}:</span>
              <span className="font-medium text-gray-900">
                {lock.warehouseCode} - {lock.warehouseName}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{t('warehouse.periodLock.period')}:</span>
              <span className="font-medium text-gray-900">{lock.periodLabel}</span>
            </div>
            <div className="flex items-start justify-between text-sm">
              <span className="text-gray-600">{t('warehouse.periodLock.lockReason')}:</span>
              <span className="font-medium text-gray-900 text-right">{lock.lockReason}</span>
            </div>
          </div>

          {/* Unlock Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.periodLock.unlockReason')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={unlockReason}
              onChange={(e) => setUnlockReason(e.target.value)}
              placeholder={t('warehouse.periodLock.reasonRequired')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
              rows={3}
              required
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? t('common.processing') : t('warehouse.periodLock.confirmUnlock')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnlockConfirmModal;
